<script lang="ts" setup>
import { Ref } from 'vue'
import WidgetWizardItemButton from './button/WidgetWizardItemButton.vue'
export interface WidgetWizardItemProps {
    data: WizardData
    index: number
}

export interface WidgetWizardItemEmits {
    (e: 'close'): void
}

export interface SelectEventData {
    autofocus: boolean
    data: WizardData
}

export interface RouteParams {
    category: string
    id: string
}

export type ButtonRefType =
    | HTMLAnchorElement
    | InstanceType<typeof WidgetWizardItemButton>

const props = defineProps<WidgetWizardItemProps>()
const emit = defineEmits<WidgetWizardItemEmits>()

const wizard = useWizard()

const attrs = useAttrs()

const buttonRef = ref<ButtonRefType>()
const closeButton = inject<Ref<HTMLButtonElement>>('modalCloseButton')
const modalContent = inject<Ref<HTMLDivElement>>('modalContent')

const route = computed<VNuxtLinkTo | null>(() =>
    useWizardItemRouteBuilder(props.data)
)

const highlightNext = (): void => {
    // if last selected - focus close button
    if (wizard.lastFocused()) {
        wizard.resetCurrentIndex()
        closeButton?.value.focus()
        return
    }
    // focus next item
    wizard.focusNext()
    return
}

const highlightPrevious = (): void => {
    // if not first selected - select previous
    if (!wizard.firstFocused()) {
        wizard.focusPrevious()
        return
    }
    // if previous exists - focus back button
    if (wizard.previousExists()) {
        wizard.setBackButtonFocusState(true)
        wizard.resetCurrentIndex()
        return
    }
    // else focus close button
    wizard.resetCurrentIndex()
    closeButton?.value.focus()
    return
}

const handleTabClick = (e: KeyboardEvent): void => {
    const { shiftKey } = e

    // handle shit + tab
    if (shiftKey) {
        highlightPrevious()
        return
    }

    // handle tab
    highlightNext()
    return
}

const handleSelect = (autofocus: boolean): void => {
    // if children exists, show
    wizard.setCurrentIndex(props.index)
    if (wizard.focusedHasChildren()) {
        if (autofocus) {
            wizard.rememberLastFocused()
        }
        wizard.switchToNext(props.data)
        wizard.setBackButtonFocusState(autofocus)
        if (modalContent?.value) {
            nextTick(() => {
                modalContent.value.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth',
                })
            })
        }
        return
    }
    // make redirect by route
    if (route.value) {
        window.open(
            useNamedRoutes(route.value),
            props.data.isExternal ? '_blank' : '_self'
        )
        if (!props.data.isExternal) {
            emit('close')
        }
        return
    }
    // make redirect by url
    if (props.data.url) {
        window.open(
            props.data.url as string,
            props.data.isExternal ? '_blank' : '_self'
        )
        if (!props.data.isExternal) {
            emit('close')
        }
    }
}

const handleFocus = () => {
    if (!wizard.someItemIsFocused()) {
        wizard.setCurrentIndex(props.index)
    }
}

const routeParams = computed<RouteParams | null>((): RouteParams | null => {
    if (!props.data.bubble?.cta?.link) return null
    const [category, id] = props.data.bubble.cta.link.split('/')
    return {
        category: category ?? '',
        id: id ?? '',
    }
})

// if item index is equal current index - focus
watch(
    (): number | null => wizard.data.value.currentIndex,
    (newVal: number | null): void => {
        const target: HTMLButtonElement | HTMLAnchorElement =
            buttonRef.value instanceof HTMLAnchorElement
                ? buttonRef.value
                : buttonRef.value?.$el
        if (newVal === props.index) {
            target?.focus()
            return
        }
        target?.blur()
    }
)

const bubbleItemButtonLink = computed<string | undefined>((): string | undefined => {

    if (props.data.bubble?.cta.link_type === 'product') {
        return useNamedRoutes({
            name: 'insurance-detail',
            params: {
                category: routeParams.value?.category,
                id: routeParams.value?.id,
            },
        })
    }
    
    return props.data.bubble?.cta.link

})

</script>

<template>
  <WidgetWizardItemButton
    v-if="!props.data.bubble || Array.isArray(props.data.bubble)"
    v-bind="attrs"
    ref="buttonRef"
    :active="wizard.data.value.currentIndex === props.index"
    :data="props.data"
    :target="props.data.isExternal ? '_blank' : '_self'"
    @select="(autofocus: boolean) => handleSelect(autofocus)"
    @focus="handleFocus"
    @tab="handleTabClick"
  />

  <WidgetWizardItemBubble
    v-else
    :data="props.data"
  >
    <template
      v-if="routeParams"
      #cta
    >
      <VButton
        ref="buttonRef"
        :target="props.data.bubble?.cta.isExternal ? '_blank' : '_self'"
        :to="bubbleItemButtonLink"
        class="wizard-item-cta btn m-0 d-inline-flex justify-content-center align-items-center fw-bolder"
        color="primary"
      >
        {{ props.data.bubble?.cta.text }}
      </VButton>
    </template>
  </WidgetWizardItemBubble>
</template>

<style scoped lang="scss">
@import './WidgetWizardItem.scss';
</style>

<style>
@import './WidgetWizardItem.vars.scss';
</style>
